import React, { useState, useEffect, useCallback } from 'react';
import type { MouseEvent } from 'react';
import { Typography } from '..';
import './accountAccordion.scss';
import { Input } from 'antd';

interface MenuItem {
  label: string;
  value?: string;
  onClick?: () => void;
}

interface AccordionProps {
  labelName?: string;
  menuItems?: MenuItem[];
  className?: string;
  theme?: 'light' | 'dark';
  isTitleStatic?: boolean;
  staticTitle?: string;
  selectedTitle?: string;
  onSelectedTitleChange?: (title: string) => void;
  searchable?: boolean;
  style?: React.CSSProperties;
}

const MemoMenuItem = React.memo(
  ({ item, onClick }: { item: MenuItem; onClick: (e: MouseEvent<HTMLLIElement>, item: MenuItem) => void }) => (
    <li onClick={(e) => onClick(e, item)}>
      <Typography variant="subHeading-4">{item.label}</Typography>
    </li>
  ),
  (prevProps, nextProps) =>
    prevProps.item.label === nextProps.item.label && prevProps.item.value === nextProps.item.value
);

const Accordion: React.FC<AccordionProps> = React.memo(
  ({
    labelName = '',
    menuItems = [],
    className = '',
    style,
    theme = 'light',
    isTitleStatic = false,
    staticTitle = '',
    selectedTitle,
    onSelectedTitleChange,
    searchable = false
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [internalSelectedTitle, setInternalSelectedTitle] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const isControlled = selectedTitle !== undefined;
    const displayedTitle = isControlled
      ? menuItems.find((item) => item.value === selectedTitle)?.label || selectedTitle
      : internalSelectedTitle;

    useEffect(() => {
      if (!isControlled) {
        const defaultTitle =
          isTitleStatic && staticTitle
            ? staticTitle
            : !isTitleStatic && menuItems.length
              ? menuItems[0].label
              : '';
        setInternalSelectedTitle(defaultTitle);
      }
    }, [isControlled, isTitleStatic, staticTitle, menuItems]);

    const toggleAccordion = useCallback(() => setIsOpen((prev) => !prev), []);

    useEffect(() => {
      if (!isOpen) return;
      const handleClickOutside = (event: Event) => {
        const accordion = document.querySelector('.accordion-wrapper');
        if (accordion && event.target && !accordion.contains(event.target as Node) &&
          (event?.target as HTMLElement)?.id !== 'accordion-search-input') {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    const handleItemClick = useCallback(
      (e: MouseEvent<HTMLLIElement>, item: MenuItem) => {
        e.stopPropagation();
        if (!isTitleStatic) {
          if (isControlled) {
            onSelectedTitleChange?.(item.value || item.label);
          } else {
            setInternalSelectedTitle(item.label);
          }
        }
        item.onClick?.();
        setIsOpen(false);
        setSearchTerm(''); // ✅ reset search when selecting
      },
      [isControlled, isTitleStatic, onSelectedTitleChange]
    );

    const filteredItems = searchable
      ? menuItems?.filter((item) => item?.label?.toLowerCase().includes(searchTerm.toLowerCase()))
      : menuItems;

    return (
      <div className="accordion-wrapper" style={style}>
        <div className={`accordion-container ${className}`} onClick={toggleAccordion}>
          <div className="label-title-container">
            <Typography variant="small-text-4" className="label">
              {labelName}
            </Typography>
            <Typography
              variant="subHeading-2"
              className={`title ${theme === 'dark' ? 'dark-text' : ''}`}
            >
              {displayedTitle || labelName}
            </Typography>
          </div>
          <div className={`arrow-icon ${theme === 'dark' ? 'dark-icon' : ''}`}>
            <i className={`bx ${isOpen ? 'bx-chevron-up' : 'bx-chevron-down'}`} />
          </div>
        </div>

        {isOpen && (
          <div className={`accordion-dropdown ${theme === 'light' ? 'dark-bg' : ''}`}>
            {searchable && (
              <Input
                id="accordion-search-input"
                type="text"
                className="accordion-search"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()} // ✅ prevent toggle when clicking search
              />
            )}
            <ul>
              {filteredItems.map((item, index) => (
                <MemoMenuItem key={index} item={item} onClick={handleItemClick} />
              ))}
              {filteredItems.length === 0 && (
                <li className="no-results">
                  <Typography variant="small-text-4">No results found</Typography>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    );
  }
);

export default Accordion;
